import { getLocalizedValue } from "@repo/shared";
import type {
  LocaleCode,
  LocalizedString,
  ProductTypeAssessment,
  ProductTypeAssessmentMode,
  UpsertProductTypeAssessmentRequest,
} from "@repo/shared/types";
import { type Dispatch, type SetStateAction, useState } from "react";

// Draft rows keep the user-language label separate from the other-language
// translations, following the LocalizedInput pattern used across settings.
export type OptionDraft = {
  id: string;
  label: string;
  i18n: LocalizedString;
  score: string;
  /** @see QuestionDraft.borrowedLabel */
  borrowedLabel: string | null;
};
export type QuestionDraft = {
  id: string;
  label: string;
  i18n: LocalizedString;
  options: OptionDraft[];
  /**
   * The trimmed text `label` was seeded with when the persisted labelI18n had
   * no entry for the user's language and getLocalizedValue borrowed another
   * one. While the field still holds exactly that text it is somebody else's
   * translation being displayed, not this user's, so it must not be written
   * back under their language key. Null once the text is genuinely theirs.
   */
  borrowedLabel: string | null;
  /** QL-253 — a generic grouping slot (1-10), or null for "None". QUIZ mode only. */
  category: number | null;
};

export const newOption = (): OptionDraft => ({
  id: crypto.randomUUID(),
  label: "",
  i18n: {},
  score: "",
  borrowedLabel: null,
});

export const newQuestion = (): QuestionDraft => ({
  id: crypto.randomUUID(),
  label: "",
  i18n: {},
  options: [newOption(), newOption()],
  borrowedLabel: null,
  category: null,
});

const isNumeric = (s: string) => s.trim() !== "" && Number.isFinite(Number(s));
const isPositiveInt = (s: string) => /^\d+$/.test(s.trim()) && Number(s) >= 1;

const optionValid = (o: OptionDraft) => o.label.trim().length > 0 && isNumeric(o.score);

export const questionIsComplete = (q: QuestionDraft) =>
  q.label.trim().length > 0 && q.options.length >= 2 && q.options.every(optionValid);

function splitI18n(
  i18n: LocalizedString | null | undefined,
  userLanguage: string,
): { value: string; rest: LocalizedString; borrowedLabel: string | null } {
  // getLocalizedValue falls back to en_GB (or any translation) so the editor
  // never shows a blank row, but a borrowed value has to be flagged — see
  // QuestionDraft.borrowedLabel.
  const own = i18n?.[userLanguage as LocaleCode]?.trim() ?? "";
  const value = getLocalizedValue(i18n || {}, userLanguage as LocaleCode);
  const rest = Object.fromEntries(
    Object.entries(i18n || {}).filter(([lang]) => lang !== userLanguage),
  );
  return { value, rest, borrowedLabel: own === "" ? value.trim() : null };
}

/** Map a persisted assessment's questions into editable drafts. */
function toDraftQuestions(
  assessment: ProductTypeAssessment,
  userLanguage: string,
): QuestionDraft[] {
  return assessment.questions.map((q) => {
    const split = splitI18n(q.labelI18n, userLanguage);
    return {
      id: q.id,
      label: split.value,
      i18n: split.rest,
      borrowedLabel: split.borrowedLabel,
      category: q.category ?? null,
      options: q.options.map((o) => {
        const oSplit = splitI18n(o.labelI18n, userLanguage);
        return {
          id: o.id,
          label: oSplit.value,
          i18n: oSplit.rest,
          score: String(o.score),
          borrowedLabel: oSplit.borrowedLabel,
        };
      }),
    };
  });
}

// Drafts rebuild each labelI18n as `{...rest, [userLanguage]: value}`, so its key
// order differs from what the server returned even when nothing changed. Key
// order must not read as an edit, hence the sorted stringify behind isDirty.
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) =>
    a < b ? -1 : a > b ? 1 : 0,
  );
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(",")}}`;
}

export type AssessmentModeDraft = {
  mode: ProductTypeAssessmentMode;
  /** The persisted row for this mode, or null when the mode is not configured yet. */
  existing: ProductTypeAssessment | null;
  enabled: boolean;
  setEnabled: (value: boolean) => void;
  questions: QuestionDraft[];
  setQuestions: Dispatch<SetStateAction<QuestionDraft[]>>;
  minScore: string;
  setMinScore: (value: string) => void;
  questionsToAsk: string;
  setQuestionsToAsk: (value: string) => void;
  maxAttempts: string;
  setMaxAttempts: (value: string) => void;
  attemptResetDays: string;
  setAttemptResetDays: (value: string) => void;
  /** QL-253 — true once any QUIZ-mode question carries a real (non-None) category. */
  hasCategories: boolean;
  /** True when this tab is safe to save — a disabled mode is always safe. */
  isValid: boolean;
  /** True when the mode is new or its fields differ from what is persisted. */
  isDirty: boolean;
  toRequest: () => UpsertProductTypeAssessmentRequest;
};

/**
 * One tab's editable state. Called once per mode by the dialog (never in a loop)
 * so a single Save can apply both modes independently.
 */
export function useAssessmentModeDraft(
  mode: ProductTypeAssessmentMode,
  existing: ProductTypeAssessment | null,
  userLanguage: string,
): AssessmentModeDraft {
  const [enabled, setEnabled] = useState(existing != null);
  const [minScore, setMinScore] = useState(existing ? String(existing.minScore) : "1");
  // A new assessment starts with no questions — the designer adds them via
  // "Add question" rather than being handed a pre-seeded empty one.
  const [questions, setQuestions] = useState<QuestionDraft[]>(() =>
    existing ? toDraftQuestions(existing, userLanguage) : [],
  );
  const [questionsToAsk, setQuestionsToAsk] = useState(
    existing ? String(existing.questionsToAsk) : "1",
  );
  const [maxAttempts, setMaxAttempts] = useState(
    existing?.maxAttempts != null ? String(existing.maxAttempts) : "",
  );
  const [attemptResetDays, setAttemptResetDays] = useState(
    existing?.attemptResetDays != null ? String(existing.attemptResetDays) : "",
  );

  const buildI18n = (
    value: string,
    rest: LocalizedString,
    borrowedLabel: string | null,
  ): LocalizedString => {
    const trimmed = value.trim();
    // Still showing another language's text untouched: leave the map alone
    // rather than duplicating it under the user's language key.
    if (borrowedLabel !== null && trimmed === borrowedLabel) return { ...rest };
    return { ...rest, [userLanguage]: trimmed };
  };

  const toRequest = (): UpsertProductTypeAssessmentRequest => ({
    mode,
    minScore: Number(minScore),
    questions: questions.map((q) => ({
      id: q.id,
      labelI18n: buildI18n(q.label, q.i18n, q.borrowedLabel),
      options: q.options.map((o) => ({
        id: o.id,
        labelI18n: buildI18n(o.label, o.i18n, o.borrowedLabel),
        score: Number(o.score),
      })),
      // QL-253 — always explicit, never omitted, so an untouched tab's
      // isDirty comparison below can't be thrown off by an old persisted
      // question that predates this field (which the server also normalizes
      // to null on read via `q.category ?? null` in toDraftQuestions).
      category: q.category ?? null,
    })),
    // Questionnaires ask every question, and the server pins the column to 1
    // for them — send that same 1 so an untouched tab compares equal below.
    questionsToAsk: mode === "QUIZ" ? Number(questionsToAsk) : 1,
    maxAttempts: mode === "QUIZ" && maxAttempts.trim() !== "" ? Number(maxAttempts) : null,
    attemptResetDays:
      mode === "QUIZ" && attemptResetDays.trim() !== "" ? Number(attemptResetDays) : null,
  });

  /** QL-253 — whether any question uses a real category (QUIZ mode only). */
  const hasCategories = mode === "QUIZ" && questions.some((q) => q.category != null);

  // --- validation (mirrors the server rules) ---
  const questionsValid = questions.length >= 1 && questions.every(questionIsComplete);
  const quizSettingsValid =
    isPositiveInt(questionsToAsk) &&
    (maxAttempts.trim() === "" || isPositiveInt(maxAttempts)) &&
    (attemptResetDays.trim() === "" || isPositiveInt(attemptResetDays));
  const fieldsValid =
    questionsValid && isNumeric(minScore) && (mode === "QUIZ" ? quizSettingsValid : true);

  const isDirty =
    existing == null ||
    stableStringify(toRequest()) !==
      stableStringify({
        mode: existing.mode,
        minScore: existing.minScore,
        // QL-253 — normalize category the same way toRequest() does (an old
        // persisted question with no category key must compare equal to the
        // draft's explicit `null`, not read as a spurious edit).
        questions: existing.questions.map((q) => ({ ...q, category: q.category ?? null })),
        questionsToAsk: existing.questionsToAsk,
        maxAttempts: existing.maxAttempts,
        attemptResetDays: existing.attemptResetDays,
      } satisfies UpsertProductTypeAssessmentRequest);

  return {
    mode,
    existing,
    enabled,
    setEnabled,
    questions,
    setQuestions,
    minScore,
    setMinScore,
    questionsToAsk,
    setQuestionsToAsk,
    maxAttempts,
    setMaxAttempts,
    attemptResetDays,
    setAttemptResetDays,
    hasCategories,
    isValid: !enabled || fieldsValid,
    isDirty,
    toRequest,
  };
}
